/**
 * BottomBar Component
 *
 * Displays executor slots and worker log updates with vertical flip animation.
 * Right-aligned log messages with dynamic update indicator.
 */

import { useEffect, useRef, useState } from "react";
import { Activity, Square, Radio, Zap } from "lucide-react";

import { dataflowApi } from "@/services/api/dataflow";
import { useDataFlowStore } from "@/stores/dataflowStore";
import { useQuery } from "@tanstack/react-query";

interface LogEntry {
  ts: string;
  kind: string;
  phase: string;
  level: string;
  instanceId: number;
  taskType: string;
  title: string;
  message: string;
}

function calcStringOverlapRatio(a: string, b: string): number {
  const left = String(a || "");
  const right = String(b || "");
  if (!left || !right) return 0;
  if (left === right) return 1;
  if (left.length === 1 || right.length === 1) {
    return left === right ? 1 : 0;
  }

  const pairs = (text: string): Map<string, number> => {
    const map = new Map<string, number>();
    for (let i = 0; i < text.length - 1; i += 1) {
      const key = text.slice(i, i + 2);
      map.set(key, (map.get(key) || 0) + 1);
    }
    return map;
  };

  const leftPairs = pairs(left);
  const rightPairs = pairs(right);
  let intersection = 0;
  for (const [key, count] of leftPairs.entries()) {
    const other = rightPairs.get(key) || 0;
    intersection += Math.min(count, other);
  }
  const total = (left.length - 1) + (right.length - 1);
  if (total <= 0) return 0;
  return (2 * intersection) / total;
}

function buildDiffSegments(previous: string, current: string): Array<{ text: string; changed: boolean }> {
  const prev = String(previous || "");
  const next = String(current || "");
  if (!next) return [];
  if (!prev) return [{ text: next, changed: false }];
  if (prev === next) return [{ text: next, changed: false }];

  let prefix = 0;
  const minLen = Math.min(prev.length, next.length);
  while (prefix < minLen && prev[prefix] === next[prefix]) {
    prefix += 1;
  }

  let suffix = 0;
  while (
    suffix < (minLen - prefix) &&
    prev[prev.length - 1 - suffix] === next[next.length - 1 - suffix]
  ) {
    suffix += 1;
  }

  const head = next.slice(0, prefix);
  const middle = next.slice(prefix, next.length - suffix);
  const middlePrev = prev.slice(prefix, prev.length - suffix);
  const tail = suffix > 0 ? next.slice(next.length - suffix) : "";
  const segments: Array<{ text: string; changed: boolean }> = [];

  if (head) segments.push({ text: head, changed: false });

  if (middle) {
    let run = "";
    let runChanged = false;
    for (let i = 0; i < middle.length; i += 1) {
      const ch = middle[i];
      const same = i < middlePrev.length && middlePrev[i] === ch;
      const changed = !same;
      if (run.length === 0) {
        run = ch;
        runChanged = changed;
        continue;
      }
      if (runChanged === changed) {
        run += ch;
      } else {
        segments.push({ text: run, changed: runChanged });
        run = ch;
        runChanged = changed;
      }
    }
    if (run) segments.push({ text: run, changed: runChanged });
  }

  if (tail) segments.push({ text: tail, changed: false });
  return segments.length > 0 ? segments : [{ text: next, changed: false }];
}

export function BottomBar() {
  const runtimeStreamEvents = useDataFlowStore((state) => state.runtimeStreamEvents);
  const runtimeStreaming = useDataFlowStore((state) => state.runtimeStreaming);
  const runtimeActiveInstances = useDataFlowStore((state) => state.runtimeActiveInstances);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevLengthRef = useRef(runtimeStreamEvents.length);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: queue } = useQuery({
    queryKey: ["dataflow", "queue"],
    queryFn: () => dataflowApi.getQueue(),
    refetchInterval: 3000,
  });

  const executorSlots = queue?.slots || [];

  // Keep recent stream events for diff context, but render latest only.
  const recentLogs = runtimeStreamEvents.slice(-30);

  // Animate only when a genuinely new event arrives; never rotate historical list.
  useEffect(() => {
    const hasNewLogs = runtimeStreamEvents.length > prevLengthRef.current;
    prevLengthRef.current = runtimeStreamEvents.length;

    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    if (!runtimeStreaming || !hasNewLogs) {
      setIsAnimating(false);
      return;
    }

    setIsAnimating(true);
    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
      animationTimeoutRef.current = null;
    }, 380);

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, [runtimeStreaming, runtimeStreamEvents.length]);

  const currentLog = recentLogs.length > 0 ? recentLogs[recentLogs.length - 1] : undefined;
  const previousLog =
    recentLogs.length > 1
      ? recentLogs[recentLogs.length - 2]
      : undefined;

  // Format log entry for display
  const formatLogEntry = (entry: LogEntry | undefined): string => {
    if (!entry) {
      if (runtimeStreaming) {
        return "任务运行中，等待实时状态事件...";
      }
      return "系统空闲，等待任务启动...";
    }
    const ts = String(entry.ts || "");
    const iid = entry.instanceId > 0 ? `#${entry.instanceId}` : "#-";
    const taskTag = entry.taskType ? `${entry.taskType}${iid}` : iid;
    const phase = entry.phase ? ` [${entry.phase}]` : "";
    const title = entry.title ? `${entry.title}: ` : "";
    return `[${ts}] [${entry.level}] [${taskTag}]${phase} ${title}${entry.message}`;
  };

  const currentText = formatLogEntry(currentLog);
  const previousText = formatLogEntry(previousLog);
  const overlapRatio = calcStringOverlapRatio(previousText, currentText);
  const shouldHighlightDiff = overlapRatio > 0.5 && Boolean(previousLog) && Boolean(currentLog);
  const shouldAnimateCurrent = isAnimating && !shouldHighlightDiff;
  const diffSegments = shouldHighlightDiff
    ? buildDiffSegments(previousText, currentText)
    : [{ text: currentText, changed: false }];

  return (
    <footer className="flex h-10 items-center justify-between border-t border-[var(--border)] bg-[#101318] px-4">
      {/* Executor Slots - Left */}
      <div className="flex items-center gap-3">
        <span className="text-[11px] text-[var(--muted)]">Executors</span>
        <div className="flex items-center gap-1">
          {executorSlots.map((slot) => (
            <ExecutorSlot
              key={slot.id}
              slot={{
                id: slot.id,
                status:
                  slot.status === "running" || slot.status === "error"
                    ? slot.status
                    : "idle",
                taskName: slot.instance_id
                  ? `${slot.task_type || "task"} #${slot.instance_id}`
                  : undefined,
              }}
            />
          ))}
          {executorSlots.length === 0 && (
            <span className="text-[10px] text-[var(--muted)]">No slot data</span>
          )}
        </div>
      </div>

      {/* Worker log display - Right aligned with vertical flip */}
      <div className="flex items-center gap-2 ml-auto h-full flex-1 justify-end">
        <div
          className="relative flex items-center h-6 overflow-hidden text-right flex-1"
          style={{ minWidth: "800px", maxWidth: "1200px" }}
        >
          <div
            key={currentText}
            className={`w-full text-[11px] text-slate-300 truncate transition-all duration-500 leading-none ${
              shouldAnimateCurrent ? "animate-log-flip-in" : ""
            }`}
            title={currentText}
          >
            {diffSegments.map((segment, idx) => (
              <span
                key={`${idx}-${segment.text}`}
                className={
                  segment.changed
                    ? "text-[#fef08a] bg-[#7c2d12]/85 px-0.5 rounded-sm"
                    : undefined
                }
              >
                {segment.text}
              </span>
            ))}
          </div>
        </div>

        {/* Dynamic update indicator */}
        <div className="flex items-center h-full">
          <LiveIndicator active={runtimeStreaming || runtimeActiveInstances > 0} />
        </div>
      </div>

      <style>{`
        @keyframes log-flip-in {
          0% {
            opacity: 0;
            transform: translateY(-100%);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-log-flip-in {
          animation: log-flip-in 0.4s ease-out;
        }
      `}</style>
    </footer>
  );
}

interface ExecutorSlotProps {
  slot: {
    id: number;
    status: "idle" | "running" | "error";
    taskName?: string;
  };
}

function ExecutorSlot({ slot }: ExecutorSlotProps) {
  const statusColors = {
    idle: "bg-slate-700/50 border-slate-600",
    running: "bg-emerald-500/20 border-emerald-500 animate-pulse",
    error: "bg-rose-500/20 border-rose-500",
  };

  const statusIcons = {
    idle: null,
    running: <Activity className="size-2.5 text-emerald-400" />,
    error: <Square className="size-2.5 text-rose-400" />,
  };

  return (
    <div
      className={`flex h-5 w-5 items-center justify-center rounded border ${statusColors[slot.status]}`}
      title={slot.taskName || `Slot ${slot.id} - ${slot.status}`}
    >
      {statusIcons[slot.status]}
    </div>
  );
}

interface LiveIndicatorProps {
  active: boolean;
}

function LiveIndicator({ active }: LiveIndicatorProps) {
  const [pulsePhase, setPulsePhase] = useState(0);

  useEffect(() => {
    if (!active) return;

    const interval = setInterval(() => {
      setPulsePhase((prev) => (prev + 1) % 4);
    }, 600);

    return () => clearInterval(interval);
  }, [active]);

  if (!active) {
    return (
      <div className="flex items-center gap-1 text-[var(--muted)] h-full">
        <Radio className="size-3" />
        <span className="text-[10px] leading-none">OFFLINE</span>
      </div>
    );
  }

  // Dynamic pulsing dots
  const dots = [0, 1, 2];

  return (
    <div className="flex items-center gap-1.5 h-full">
      {dots.map((i) => (
        <div
          key={i}
          className={`size-1.5 rounded-full transition-all duration-300 ${
            pulsePhase === i
              ? "bg-emerald-400 scale-125 shadow-[0_0_6px_rgba(52,211,153,0.8)]"
              : pulsePhase === (i + 1) % 4
              ? "bg-emerald-400/60"
              : "bg-emerald-400/20"
          }`}
        />
      ))}
      <Zap
        className={`size-3 ml-0.5 transition-all duration-300 ${
          pulsePhase % 2 === 0
            ? "text-emerald-400 fill-emerald-400/30"
            : "text-emerald-400/60"
        }`}
      />
    </div>
  );
}
